import { useMemo } from 'react';
import { useModels } from 'src/features/models/queries/useModels';
import { useFlowContext } from 'src/features/flows/contexts/FlowContext';
import { PredictionParameters } from 'src/features/flows/types/flowParameter.types';

/**
 * Custom React hook to generate prediction parameters based on models data. Used in the condition node to render the
 * possible models options to filter on.
 *
 * NOTE: The memoization here prevents re-computation when availableModels array reference
 * changes but content is the same (which happens frequently due to context updates).
 * This is critical for performance when many components subscribe to this data.
 */
export function usePredictionParameters() {
  const models = useModels();
  const { availableModels } = useFlowContext();

  // ✅ Memoize the computed data to prevent recalculation
  // When availableModels array gets a new reference but same content,
  // this prevents expensive recomputation
  const predictionParameters = useMemo<PredictionParameters[] | undefined>(() => {
    if (!models.data) return undefined;

    const predictionParameters: PredictionParameters[] = [];

    availableModels.forEach((model) => {
      if (model.settings?.ner || model.settings?.modelType === 'ner') {
        return; // todo: implement this
      } else {
        predictionParameters.push({
          prefix: 'facet',
          name: model.id + '.label',
          displayName: model.displayName,
          type: 'string',
          options: model.tags
            .sort((a, b) => (a.shortName || a.internalId).localeCompare(b.shortName || b.internalId))
            .map((tag) => ({
              id: tag.internalId,
              displayName: tag.shortName || tag.internalId,
            })),
        });
      }
    });

    return predictionParameters;
  }, [availableModels, models.data]);

  // ✅ Memoize the return object to maintain stable reference
  // Only creates new object when data or loading state actually changes
  return useMemo(() => ({ ...models, data: predictionParameters }), [models, predictionParameters]);
}
